import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {UpdateChildPlanDataCommand} from 'src/app/domain/command/child/child.command';
import {createUpdateChildDataFromFixture} from 'src/app/domain/command/child/createUpdateChildDataFromPlan';
import {createLeadDataFromFixture} from 'src/app/domain/command/lead/createLeadDataFromLead';
import {UpdateLeadDataCommand} from 'src/app/domain/command/lead/UpdateLeadDataCommand';
import {ChildFixture} from 'src/app/domain/model/child/child-fixture.model';
import {Activity} from 'src/app/domain/model/customer/activity.model';
import {UserFixture} from 'src/app/domain/model/user/user-fixture.model';
import CustomerFormValuesResolver from 'src/app/infrastructure/form/customer-values-resolver.service';
import {ApiClient} from 'src/app/infrastructure/http/api.client';
import {InputListItem} from 'src/app/ui/shared/component/input/input-list-item';

@Injectable({
  providedIn: 'root'
})
export default class FixturesApi {
  private readonly portfolioUrl = 'portfolio';
  private readonly accountUrl = 'account';

  constructor(
    private http: ApiClient,
    private httpExternal: HttpClient,
    public valuesResolver: CustomerFormValuesResolver,
  ) {
  }

  public async completeRebalance(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/complete-rebalance');
  }

  public async activateAccount(accountId: string): Promise<void> {
    await this.http.post(this.accountUrl + '/' + accountId + '/fixtures/activate');
  }

  public async simulateCashTransferIn(portfolioId: string, amount: number = 15000): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/cash-transfer-in', {amount});
  }

  public async createFinizensFee(portfolioId: string, amount: number): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/create-finizens-fee', {amount});
  }

  public async payFinizensFee(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/pay-finizens-fee');
  }

  public async positions(portfolioId: string, days: number = 1): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/positions', {days});
  }

  public async obtainIban(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/set-external-id');
  }

  public async processOrder(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/process-order');
  }

  public async completeOrder(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/complete-order');
  }

  public async submitWithdrawal(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/submit-withdrawal');
  }

  public async enableAccount(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/enable');
  }

  public async enableChangePortfolio(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/enable-change-portfolio');
  }

  public async completeFundTransfer(portfolioId: string): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/complete-fund-transfer');
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  public async createOutgoingTransfer(portfolioId: string, transferType: string, numberOfFunds: number): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/create-outgoing-transfer', {
      transfer_type: transferType,
      number_of_funds: numberOfFunds
    });
  }

  public async createIncomingTransfer(portfolioId: string, transferType: string, numberOfFunds: number): Promise<void> {
    await this.http.post(this.portfolioUrl + '/' + portfolioId + '/fixtures/create-incoming-transfer', {
      transfer_type: transferType,
      number_of_funds: numberOfFunds
    });
  }
  /* eslint-enable @typescript-eslint/naming-convention */

  public userFixture(): Observable<UserFixture>{
    return this.httpExternal.get<any>('https://randomuser.me/api/?nat=es&noinfo').pipe(
        map(response => response.results[0] as UserFixture)
      );
  }

  public userDataCommand(userFixture: UserFixture, numChildrenList: InputListItem[], activities: Activity[]): UpdateLeadDataCommand{
    const activity = activities[Math.floor(Math.random() * activities.length)];
    const additionalParams = {
      maritalStatus: this.valuesResolver.getMaritalStatusList()[Math.floor(Math.random() * this.valuesResolver.getMaritalStatusList().length)],
      children: numChildrenList[Math.floor(Math.random() * numChildrenList.length)].value,
      activity: activity.id,
      fundsOrigin: this.valuesResolver.getFundsOriginList()[Math.floor(Math.random() * this.valuesResolver.getFundsOriginList().length)],
      employmentStatus: this.valuesResolver.getEmploymentStatusList()[Math.floor(Math.random() * this.valuesResolver.getEmploymentStatusList().length)],
      incomeVolume: this.valuesResolver.getIncomeVolumeList()[Math.floor(Math.random() * this.valuesResolver.getIncomeVolumeList().length)],
      incomeOrigin: this.valuesResolver.getIncomeOriginList()[Math.floor(Math.random() * this.valuesResolver.getIncomeOriginList().length)],
      publicCharge: this.valuesResolver.getPublicChargeList()[Math.floor(Math.random() * this.valuesResolver.getPublicChargeList().length)],
      vat: this.calculateLetterDni(userFixture?.id.value.slice(0, -2)),
      nationality1: 'ES',
      country: 'ES',
      countryOfBirth: 'ES',
      isCompanyManager: false
    };

    return createLeadDataFromFixture(userFixture, additionalParams);
  }

  public childDataCommand(childFixture: ChildFixture): UpdateChildPlanDataCommand {
    const additionalParams = {
      vat: this.calculateLetterDni(childFixture?.id.value.slice(0, -2)),
      nationality1: 'ES',
      country: 'ES'
    };

    return createUpdateChildDataFromFixture(childFixture, additionalParams);
  }

  private calculateLetterDni(numeroDNI: string): string {
    const dniLetters: string[] = ['T', 'R', 'W', 'A', 'G', 'M', 'Y', 'F', 'P', 'D', 'X', 'B', 'N', 'J', 'Z', 'S', 'Q', 'V', 'H', 'L', 'C', 'K', 'E'];
    return numeroDNI+dniLetters[parseInt(numeroDNI, 10) % 23];
  }
}

export const getZipCodeFormatted = (zipCode: number): string => {
  const minZip = 1000;
  const maxZip = 52999;
  const zipCodeFormatted = maxZip >= zipCode
    ? zipCode.toString()
    : (Math.floor(Math.random() * (maxZip - minZip + 1)) + minZip).toString();
  return ('0' + zipCodeFormatted).slice(-5);
};
